import {useEffect, useState} from "react";
import {Box, Button, Dialog, DialogContent, IconButton, Typography} from "@mui/material";
import {Close as CloseIcon, Business as CompaniesIcon} from "@mui/icons-material";
import {RTL} from "../../cache/CacheRtl";
import ImpersonationCompaniesPicker from "./ImpersonationCompaniesPicker";
import {updateCompanyGroupAdminCompanies} from "../../services/system/CompanyGroupAdminService";
import {useMessageContext} from "../../contexts/MessageContext";
import {getErrorMessage} from "../../enums/ErrorMessages";
import {colors, borders} from "../ui/colors";

// Super-admin dialog to edit the set of companies a group admin may impersonate (add + remove),
// reusing the exact picker from the creation flow. Pre-selects the admin's current companies.
const EditAuthorizedCompaniesDialog = ({open, close, admin, onSaved}) => {
    const {notificationHandler} = useMessageContext();
    const [selectedIds, setSelectedIds] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSelectedIds((admin?.companies || []).map((c) => c.id));
    }, [open, admin]);

    const handleSave = () => {
        setSaving(true);
        updateCompanyGroupAdminCompanies(admin.id, selectedIds)
            .then(() => notificationHandler.success("החברות המורשות עודכנו בהצלחה"))
            .then(() => onSaved?.())
            .then(close)
            .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)))
            .finally(() => setSaving(false));
    };

    return (
        <Dialog
            open={open}
            onClose={close}
            PaperProps={{sx: {borderRadius: borders.radius.lg, minWidth: {xs: "90%", sm: "480px"}, maxWidth: {xs: "95%", md: "560px"}, maxHeight: {xs: "90vh", md: "85vh"}, overflowY: "auto"}}}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", backgroundColor: colors.bg, borderBottom: borders.default}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <CompaniesIcon sx={{color: colors.textSecondary, fontSize: "20px"}}/>
                            <Typography sx={{fontWeight: 600, fontSize: "16px", color: colors.textPrimary}}>עריכת חברות מורשות להתחזות</Typography>
                        </Box>
                        <IconButton onClick={close} size="small" sx={{color: colors.textMuted, "&:hover": {backgroundColor: colors.bgHover, color: colors.textPrimary}}}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <DialogContent sx={{padding: "24px 20px"}}>
                        <ImpersonationCompaniesPicker active={open} value={selectedIds} onChange={setSelectedIds}/>
                    </DialogContent>

                    {/* Footer */}
                    <Box sx={{display: "flex", justifyContent: "flex-start", gap: 1.5, padding: "16px 20px", borderTop: borders.default, backgroundColor: colors.bg}}>
                        <Button onClick={handleSave} disabled={saving} variant="contained"
                            sx={{backgroundColor: colors.primary, color: colors.white, fontSize: "13px", fontWeight: 600, padding: "8px 24px", borderRadius: borders.radius.md, textTransform: "none", "&:hover": {backgroundColor: colors.primaryDark}, "&.Mui-disabled": {backgroundColor: colors.border, color: colors.textMuted}}}>
                            שמור
                        </Button>
                        <Button onClick={close} variant="outlined"
                            sx={{borderColor: colors.border, color: colors.textSecondary, fontSize: "13px", fontWeight: 500, padding: "8px 20px", borderRadius: borders.radius.md, textTransform: "none", "&:hover": {borderColor: colors.textSecondary, backgroundColor: colors.bgHover}}}>
                            ביטול
                        </Button>
                    </Box>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default EditAuthorizedCompaniesDialog;
